// Supporter and school licence configuration (F13, F15). Set at build time with VITE_LS_* environment variables
// (see .env.example). Owner-controlled: change product IDs or prices only with the owner's say-so (CLAUDE.md).
import type { Tier } from './tier';

const env = import.meta.env as Record<string, string | undefined>;
const ids = (v: string | undefined) => (v ?? '').split(',').map((s) => s.trim()).filter(Boolean).map(Number);

export const LICENCE_API = 'https://api.lemonsqueezy.com/v1/licenses';

export type StoreConfig = {
  storeId: number;
  supporterProducts: number[];
  schoolProducts: number[];
  checkout: { oneOff: string; monthly: string; school: string };
  portal: string;
};

/** Build-time defaults (dev server reads .env). Production reads /store.json, written at build from the same variables. */
export const STORE: StoreConfig = {
  storeId: Number(env.VITE_LS_STORE_ID ?? 0),
  supporterProducts: ids(env.VITE_LS_SUPPORTER_PRODUCT_IDS),
  schoolProducts: ids(env.VITE_LS_SCHOOL_PRODUCT_IDS),
  checkout: {
    oneOff: env.VITE_LS_CHECKOUT_ONE_OFF ?? '',
    monthly: env.VITE_LS_CHECKOUT_MONTHLY ?? '',
    school: env.VITE_LS_CHECKOUT_SCHOOL ?? '',
  },
  portal: env.VITE_LS_PORTAL_URL ?? '',
};

let loading: Promise<StoreConfig> | null = null;
/** Loads /store.json once (falls back to the build-time defaults). Mutates STORE so every reader sees it. */
export function loadStore(): Promise<StoreConfig> {
  if (STORE.storeId || typeof fetch === 'undefined' || typeof location === 'undefined') return Promise.resolve(STORE);
  loading ??= fetch('/store.json')
    .then((r) => (r.ok ? (r.json() as Promise<Partial<StoreConfig>>) : {}))
    .then((c) => Object.assign(STORE, c))
    .catch(() => STORE);
  return loading;
}

/** Display prices in Australian dollars. Checkout converts to local currency and adds tax (the provider is merchant of record). */
export const PRICES = { oneOff: 29, monthly: 6, school: 149 } as const;
export const aud = (n: number) => `A$${n}`;

export const configured = () => STORE.storeId > 0 && STORE.supporterProducts.length + STORE.schoolProducts.length > 0;

export function tierForProduct(productId: number): Exclude<Tier, 'none'> | null {
  if (STORE.schoolProducts.includes(productId)) return 'school';
  if (STORE.supporterProducts.includes(productId)) return 'supporter';
  return null;
}
